import { ArrowUpRight, Clock, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ORADEA_TOURS, APUSENI_TOURS } from "../../data/experiences";
import { formatPrice } from "../../i18n/pricing";
import { LocalizedLink } from "../../lib/i18nRouting";
import Reveal from "../Reveal";
import { HOLIDAYS_PATH, HOLIDAY_PACKAGES } from "../../data/holidays";
import { useEarlyBird } from "../../lib/useEarlyBird";

export default function Experiences() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <section className="bg-bone py-24 lg:py-40" id="experiente" data-testid="experiences-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-16 lg:mb-24">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-wider-3 text-moss mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-moss" />
              {t("home_experiences.eyebrow")}
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-forest leading-[1.04]">
              {t("home_experiences.title_a")}<br/>
              <span className="italic">{t("home_experiences.title_b")}</span>
            </h2>
          </div>
          <div className="md:col-span-5 text-muted-foreground text-base leading-relaxed">
            {t("home_experiences.intro")}
          </div>
        </Reveal>

        <CategoryRow
          number={t("home_experiences.oradea.eyebrow")}
          title={t("home_experiences.oradea.title")}
          subtitle={t("home_experiences.oradea.subtitle")}
          items={ORADEA_TOURS}
          lang={lang}
          t={t}
        />

        <div className="mt-24 lg:mt-32">
          <HolidaysRow t={t} />
        </div>
      </div>
    </section>
  );
}

function CategoryRow({ number, title, subtitle, items, lang, t }) {
  return (
    <div>
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-3">{number}</div>
          <h3 className="font-display text-3xl md:text-4xl text-forest">{title}</h3>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">{subtitle}</p>
        </div>
        <LocalizedLink
          to="/experiente"
          data-testid={`see-all-${number.toLowerCase()}`}
          className="text-xs uppercase tracking-wider-3 text-forest link-underline inline-flex items-center gap-2"
        >
          {t("common.see_all")} <ArrowUpRight className="w-4 h-4" />
        </LocalizedLink>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {items.map((exp, i) => {
          const price = formatPrice(exp.price, lang, exp.slug);
          const duration = t(`tours.${exp.slug}.duration`);
          const group = t(`tours.${exp.slug}.groupSize`);
          const name = t(`tours.${exp.slug}.name`);
          const short = t(`tours.${exp.slug}.short`);
          return (
            <Reveal key={exp.slug} delay={i * 120}>
              <LocalizedLink
                to={`/experiente/${exp.slug}`}
                data-testid={`experience-card-${exp.slug}`}
                className="group block bg-white border border-bone-deep h-full"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-stone-warm">
                  <img src={exp.image} alt={name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
                  <div className="absolute top-4 left-4 bg-bone/95 text-forest text-[10px] uppercase tracking-wider-2 px-3 py-1">
                    {duration}
                  </div>
                  <div className="absolute top-4 right-4 bg-forest text-bone text-[10px] uppercase tracking-wider-2 px-3 py-1">
                    {price} {t("common.per_person")}
                  </div>
                </div>
                <div className="p-6 lg:p-8">
                  <h4 className="font-display text-2xl md:text-3xl text-forest leading-tight">{name}</h4>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{short}</p>
                  <div className="mt-6 pt-5 border-t border-bone-deep flex items-center justify-between text-[11px] uppercase tracking-wider-2 text-moss">
                    <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{duration}</span>
                    <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" />{group}</span>
                  </div>
                </div>
              </LocalizedLink>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}

// Homepage "Multi-day adventures" row — shows the 2027 E-MTB Holidays packages (standard public prices).
// Same card layout as CategoryRow; images reuse the existing Apuseni tour photos; every link goes to the 2027 landing page.
function HolidaysRow({ t }) {
  const k = "home_experiences.apuseni";
  const eb = useEarlyBird();
  return (
    <div data-testid="home-holidays-row">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-3">{t(`${k}.eyebrow`)}</div>
          <h3 className="font-display text-3xl md:text-4xl text-forest">{t(`${k}.title`)}</h3>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">{t(`${k}.subtitle`)}</p>
        </div>
        <LocalizedLink to={HOLIDAYS_PATH} data-testid="see-all-apuseni" className="text-xs uppercase tracking-wider-3 text-forest link-underline inline-flex items-center gap-2">
          {t("common.see_all")} <ArrowUpRight className="w-4 h-4" />
        </LocalizedLink>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {HOLIDAY_PACKAGES.map((p, i) => {
          const c = `${k}.cards.${p.key}`;
          return (
            <Reveal key={p.key} delay={i * 120}>
              <LocalizedLink to={HOLIDAYS_PATH} data-testid={`holiday-card-${p.key}`} className="group block bg-white border border-bone-deep h-full">
                <div className="relative aspect-[4/5] overflow-hidden bg-stone-warm">
                  <img src={APUSENI_TOURS[i].image} alt={t(`${c}.title`)} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
                  <div className="absolute top-4 left-4 bg-bone/95 text-forest text-[10px] uppercase tracking-wider-2 px-3 py-1">{t(`${c}.label`)}</div>
                  <div className="absolute top-4 right-4 bg-forest text-bone text-[10px] uppercase tracking-wider-2 px-3 py-1" data-testid={`holiday-card-price-${p.key}`}>
                    {t(`${c}.price`)} {t(`${k}.per_person`)}
                  </div>
                </div>
                <div className="p-6 lg:p-8">
                  <h4 className="font-display text-2xl md:text-3xl text-forest leading-tight">{t(`${c}.title`)}</h4>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t(`${c}.text`)}</p>
                  <div className="mt-6 pt-5 border-t border-bone-deep flex items-center justify-between text-[11px] uppercase tracking-wider-2 text-moss">
                    <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" />{t(`${c}.label`)}</span>
                    <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" />{t(`${k}.riders`)}</span>
                  </div>
                </div>
              </LocalizedLink>
            </Reveal>
          );
        })}
      </div>

      {eb.active && <Reveal className="mt-8 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm" data-testid="home-holidays-early-bird">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
        <span className="text-forest">{t(`${k}.early_bird`)}</span>
        <LocalizedLink to={HOLIDAYS_PATH} data-testid="home-holidays-early-bird-link" className="text-xs uppercase tracking-wider-3 text-terracotta link-underline inline-flex items-center gap-1">
          {t(`${k}.early_bird_cta`)} <ArrowUpRight className="w-3.5 h-3.5" />
        </LocalizedLink>
      </Reveal>}
    </div>
  );
}
